import { ReactNode, useEffect, useState } from 'react';
import { cn } from '../../lib/utils';
import { Popover, PopoverContent, PopoverAnchor } from '../ui/Popover';

interface MenuPopoverProps {
  open?: boolean | HTMLElement | null;
  onOpen?: () => void;
  onClose?: () => void;
  children: ReactNode;
  /** Optional class for the popover content (e.g. min-w-96 w-auto for a wider popover). */
  contentClassName?: string;
  arrow?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right'
    | 'left-top'
    | 'left-center'
    | 'left-bottom'
    | 'right-top'
    | 'right-center'
    | 'right-bottom';
  disabledArrow?: boolean;
}

export default function MenuPopover({
  open,
  onOpen,
  onClose,
  children,
  contentClassName,
  arrow: _arrow,
  disabledArrow: _disabledArrow
}: MenuPopoverProps) {
  // Convert HTMLElement | null to boolean for compatibility with old code
  const isOpen = Boolean(open);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (open && open instanceof HTMLElement) {
      setAnchorRect(open.getBoundingClientRect());
    } else if (!open && anchorRect) {
      // Delay clearing the anchor until after the close animation completes
      const timer = setTimeout(() => {
        setAnchorRect(null);
      }, 200); // Match the animation duration
      return () => clearTimeout(timer);
    }
  }, [open, anchorRect]);

  return (
    <Popover open={isOpen} onOpenChange={(opened) => (opened ? onOpen?.() : onClose?.())}>
      {anchorRect && (
        <PopoverAnchor
          style={{
            position: 'fixed',
            left: anchorRect.left,
            top: anchorRect.top,
            width: anchorRect.width,
            height: anchorRect.height,
            pointerEvents: 'none'
          }}
        />
      )}
      <PopoverContent
        className={cn('p-1', contentClassName)}
        side="right"
        align="start"
        collisionPadding={16}
        avoidCollisions={true}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
